import { GoogleMap } from '@react-google-maps/api'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import styled from 'styled-components'

import TripItemMapRouteComponent from './MapElement/TripItemMapRoute'
import TripItemMapMarkerComponent from './MapElement/TripItemMapMarker'
import ExploreCardCarousel from './MapElement/ExploreCardCarousel'
import ExploreCardSingle from './MapElement/ExploreCardSingle'
import ExploreMapMarker from './MapElement/ExploreMapMarker'
import GooglePOICardSingle from './MapElement/GooglePOICardSingle'
import GooglePOIMarker from './MapElement/GooglePOIMarker'
import SearchCardSingle from './MapElement/SearchCardSingle'
import SearchMapMarker from './MapElement/SearchMapMarker'
import TripItemCardSingle from './MapElement/TripItemCardSingle'
import MapSearch from './MapSearch/MapSearch'
import MapControlButtons from './MapControlButtons/MapControlButtons'
import type { TripItemMapMarker } from './utils'
import {
  isMapItem,
  isExploreMarker,
  isRoute,
  filterMapElements,
  findNewOrMovedTripItems,
} from './utils'
import TooltipModal from '../TripModal/TooltipModal'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useGoogleMapsContext } from 'contexts/GoogleMapsContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { arrayToMap, EmptyArray } from 'lib/array/arrayUtils'
import {
  DEFAULT_MAP_OPTIONS,
} from 'tripPlanner/config/map'
import { useTripExplorePlaceDetails } from 'tripPlanner/hooks/Google/useTripExplorePlaceDetail'
import {
  clearCurrentSelectionId,
  setCurrentSelectionId,
} from 'tripPlanner/reducers/actions'
import type { ExploreItem, FullTrip, ItemWithOnlyLatLng } from 'tripPlanner/types/common'
import type { PublicFullTrip, PublicMapItem } from 'tripPlanner/types/publicTrip'
import type { MapItem } from 'tripPlanner/types/tripItem'
import {
  getPlaceIdToSavedExploreItemMap,
} from 'tripPlanner/utils/explore'
import { exploreItemSelectionId, leOfferSelectionId, tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import type { TripTab } from 'tripPlanner/utils/tabs'
import MapLayersButton from './MapLayers/MapLayersButton'
import MapLayersContext from 'tripPlanner/contexts/MapLayers/MapLayersContext'
import type { TripMapContextValue } from 'tripPlanner/contexts/TripMapContext'
import { TripMapContext } from 'tripPlanner/contexts/TripMapContext'
import useExplorePins from './hooks/useExplorePins'
import useTripItemMapElements from './hooks/useTripItemMapElements'
import useAutoMapBounds from './hooks/useAutoMapBounds'
import useMapSearchState from './hooks/useMapSearchState'
import { getTripFilters } from 'tripPlanner/utils/selectors'
import { getCurrentUserId } from 'selectors/accountSelectors'
import usePrevious from 'hooks/usePrevious'
import debounce from 'lodash.debounce'
import LEOfferMapMarker from './MapElement/LEOfferMapMarker'
import useLEExperiencePins from './hooks/useLEExperiencePins'
import LEOfferCard from './MapElement/LEOfferCard'
import ProductLayersChips from './MapLayers/ProductLayersChips'
import useLEHotelPins from './hooks/useLEHotelPins'
import { rem } from 'polished'
import { DesktopLayoutSizes } from '../ItemCard/Common/utils'
import zIndex from 'styles/tools/z-index'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import DayRoutes from './MapElement/DayRoutes'

const containerStyle = {
  height: '100%',
  minHeight: '80vh',
}

const ModalContainer = styled.div`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

export const DesktopRoot = styled.div`
  height: 100%;
`

const CardContainer = styled.div`
  position: absolute;
  z-index: ${zIndex.header};
  padding: 0 ${rem(16)} ${rem(16)};
  bottom: 0;
  width: 100%;
  pointer-events: none;
  display: flex;
  justify-content: center;

  ${DesktopLayoutSizes} {
    padding: 0 ${rem(24)} ${rem(24)};
  }

  & > * {
    pointer-events: auto;
  }
`

export interface CustomModalConfig {
  title?: string
  closeModalAction?: () => void
  backButtonAction?: () => void
}

export interface MapProps {
  title: string
  tab: TripTab
  trip: FullTrip | PublicFullTrip
  canEdit: boolean
  mapContainerClassName?: string
  currentSelectionId?: string
  /**
   * Currently only affects explore items.
   * - If true, the map will use a carousel to display explore item cards.
   * - If false, selecting an explore item will bring up a single card.
   */
  useCardCarousel: boolean
  updateModalConfig?: (value: CustomModalConfig) => void
  isTripLoading?: boolean
}

function TripMap({
  trip,
  canEdit,
  tab = 'Summary',
  mapContainerClassName,
  currentSelectionId,
  useCardCarousel,
  updateModalConfig,
  isTripLoading = false,
}: ConnectedProps<typeof connector>) {
  const items = useMemo(() => trip.items.filter(isMapItem) as Array<MapItem | PublicMapItem>, [trip.items])
  const { showAllRoutes } = useContext(MapLayersContext)

  const tripHasDates = !!(trip.startDate && trip.endDate)
  const showItineraryAsSummary = !tripHasDates
  const filters = useAppSelector(getTripFilters)

  const tripId = trip.id
  const dispatch = useAppDispatch()

  const [POIItem, setPOIItem] = useState<
    ExploreItem | ItemWithOnlyLatLng | null | undefined
  >() // While loading, this item will only have lat and long values.

  const mapContext = useMemo<TripMapContextValue>(() => ({
    onSelectMapItem: (sectionId: string) => {
      dispatch(setCurrentSelectionId(sectionId))
    },
    onDeselectMapItem: () => {
      dispatch(clearCurrentSelectionId())
      setPOIItem(undefined)
    },
  }), [dispatch])

  const [isZooming, setIsZooming] = useState<boolean>(false) // nothing uses this state, but without it, flight paths don't render.
  const [userAdjustedViewport, setUserAdjustedViewport] = useState<boolean>(false)
  const mapRef = useRef<google.maps.Map | undefined>(undefined)

  const { isLoaded: isMapsContextLoaded } = useGoogleMapsContext()

  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false)

  const {
    exploreItems,
    exploreCategory,
    exploreMarkers,
    exploreIsLoading,
  } = useExplorePins(tab)

  const exploreItemsToUse: Array<ExploreItem> = tab === 'Explore' ? exploreItems : EmptyArray

  const { layerGroups } = useContext(MapLayersContext)
  const layerGroupsToUse = tab !== 'Explore' ? layerGroups : EmptyArray

  const allTripItemMarkersWithoutExplorePlaces = useTripItemMapElements(trip, tab, exploreItemsToUse)

  const currentUserId = useAppSelector(getCurrentUserId)!
  const prevItems = usePrevious(trip.items, trip.items)
  const [mountedAt] = useState<moment.Moment>(() => moment())
  const newOrMovedTripItems = useMemo(() => findNewOrMovedTripItems(currentUserId, trip.items, prevItems, mountedAt), [currentUserId, trip.items, prevItems, mountedAt])
  const isActivelyGenerating =
    trip?.generationStartedAt !== undefined && trip?.generationStatus === 'PENDING'

  useEffect(() => {
    if (newOrMovedTripItems.length > 0 && !isActivelyGenerating) {
      const itemIdToSelect = newOrMovedTripItems[0].id
      if (allTripItemMarkersWithoutExplorePlaces.some(marker => marker.item.id === itemIdToSelect)) {
        dispatch(setCurrentSelectionId(tripItemSelectionId(newOrMovedTripItems[0].id)))
      }
    }
  }) // No need for deps - when there's a new or moved item, newOrMovedTripItems will only be non-empty for one render

  const allTripItemAndExploreMapElements = useMemo(() => {
    return [
      ...allTripItemMarkersWithoutExplorePlaces,
      ...exploreMarkers,
    ]
  }, [allTripItemMarkersWithoutExplorePlaces, exploreMarkers])

  const filtersToUse: Array<App.TripPlannerProductFilterType> = tab === 'Itinerary' && showItineraryAsSummary ? filters : EmptyArray

  const tripItemAndExploreMapElements = useMemo(() => {
    return filterMapElements(trip, allTripItemAndExploreMapElements, layerGroups, filtersToUse)
  }, [allTripItemAndExploreMapElements, filtersToUse, layerGroups, trip])

  const [bounds, setBounds] = useState<string | undefined>()
  const onBoundsChanged = useMemo<() => void>(() => debounce(
    () => {
      const map = mapRef.current
      if (map) {
        setBounds(map.getBounds()?.toUrlValue())
      }
    },
    2000,
  ), [])

  const hotelMarkers = useLEHotelPins(trip, layerGroups, bounds, currentSelectionId)
  const experienceMarkers = useLEExperiencePins(trip, layerGroups, bounds, currentSelectionId)
  const offerMarkers = useMemo(() => [...hotelMarkers, ...experienceMarkers], [hotelMarkers, experienceMarkers])

  useAutoMapBounds(mapRef, isMapLoaded, tab, isTripLoading, exploreIsLoading, exploreItemsToUse, tripItemAndExploreMapElements, allTripItemAndExploreMapElements, newOrMovedTripItems, userAdjustedViewport)

  const tripItemMarkersBySelectionId = useMemo(() => arrayToMap(allTripItemMarkersWithoutExplorePlaces, marker => tripItemSelectionId(marker.item.id)), [allTripItemMarkersWithoutExplorePlaces])
  const exploreMarkersBySelectionId = useMemo(() => arrayToMap(exploreMarkers, marker => exploreItemSelectionId(marker.item.id)), [exploreMarkers])
  const offerMarkersBySelectionId = useMemo(() => arrayToMap(offerMarkers, marker => leOfferSelectionId(marker.offer)), [offerMarkers])

  const activeExploreMarker = exploreMarkersBySelectionId.get(currentSelectionId as string)
  const activeTripItemMarker = tripItemMarkersBySelectionId.get(currentSelectionId as string) as TripItemMapMarker | undefined
  const activeOfferMarker = offerMarkersBySelectionId.get(currentSelectionId as string)

  const savedExploreItemsByPlaceId = getPlaceIdToSavedExploreItemMap(items)

  const cardContainerRef = useRef<HTMLDivElement | null>(null)

  const {
    setMapSearchItem,
    mapSearchMarker,
    clearMapSearchItem,
  } = useMapSearchState(mapRef, items, exploreItems, useCardCarousel)

  const { data: fullPoiItems } = useTripExplorePlaceDetails({
    placeIds: [POIItem?.id],
  })

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map
      setIsMapLoaded(true)
      setBounds(map.getBounds()?.toUrlValue())
      map.addListener('click', (event: google.maps.IconMouseEvent) => {
        if (!event.placeId) {
          return
        }

        if (event.latLng) {
          // realistically, if they are clicking a point on the map...
          // it will have a lat and lng value.
          setPOIItem({
            id: event.placeId,
            place: { point: event.latLng?.toJSON() },
          })
        }

        dispatch(setCurrentSelectionId(exploreItemSelectionId(event.placeId)))
        event.stop()
      })
    },
    [dispatch],
  )

  const showLayersButton = (!useCardCarousel || !activeExploreMarker) && layerGroupsToUse.length > 0 && tab !== 'Explore'

  const tripDates = useMemo(() => {
    if (!trip.startDate || !trip.endDate) return []
    return Array.from(new Set(
      trip.items
        .filter(item => item.startDate)
        .map(item => moment(item.startDate).format(ISO_DATE_FORMAT)),
    )).sort()
  }, [trip])

  return (
    <TripMapContext.Provider value={mapContext}>
      {isMapsContextLoaded && (
        <GoogleMap
          options={DEFAULT_MAP_OPTIONS}
          onLoad={onLoad}
          onIdle={() => setIsZooming(false)}
          onZoomChanged={() => { setIsZooming(true); setUserAdjustedViewport(true) }}
          onDragStart={() => setUserAdjustedViewport(true)}
          mapContainerStyle={containerStyle}
          mapContainerClassName={mapContainerClassName}
          onBoundsChanged={onBoundsChanged}
        >
          <MapSearch
            mapRef={mapRef}
            tripId={tripId}
            updateModalConfig={updateModalConfig}
            setMapSearchItem={setMapSearchItem}
          />
          <ProductLayersChips />
          <MapControlButtons mapRef={mapRef} />
          {tripItemAndExploreMapElements.map((marker, i) => {
            if (isRoute(marker)) {
              return (
                <TripItemMapRouteComponent
                  zIndex={i}
                  key={`route-${i}`}
                  item={marker.item}
                  position={marker.position}
                  active={tripItemSelectionId(marker.item.id) === currentSelectionId}
                  map={mapRef.current}
                  isZooming={isZooming}
                />
              )
            } else if (isExploreMarker(marker)) {
              return (
                <ExploreMapMarker
                  zIndex={i}
                  key={`marker-${marker.item.id}`}
                  category={exploreCategory}
                  item={marker.item}
                  position={marker.position}
                  active={exploreItemSelectionId(marker.item.id) === currentSelectionId}
                  saved={savedExploreItemsByPlaceId.has(marker.item.place.id as string)}
                />
              )
            } else {
              const markerPlace = 'place' in marker.item ? marker.item.place : marker.item.startPlace
              return (// hidden if its the same as the current search marker
                markerPlace &&
                mapSearchMarker?.item.id !== markerPlace.id && (<TripItemMapMarkerComponent
                  zIndex={i}
                  key={`marker-${marker.item.id}-${marker.itemPlaceType}`}
                  item={marker.item}
                  position={marker.position}
                  active={tripItemSelectionId(marker.item.id) === currentSelectionId}
                  tab={tab}
                  map={mapRef.current}
                />))
            }
          })}

          {offerMarkers.map((marker, i) => (
            <LEOfferMapMarker
              zIndex={tripItemAndExploreMapElements.length + i}
              key={`marker-${marker.offer.id}`}
              offer={marker.offer}
              position={marker.position}
              active={leOfferSelectionId(marker.offer) === currentSelectionId}
            />
          ))}

          {mapSearchMarker && (
            <SearchMapMarker
              zIndex={tripItemAndExploreMapElements.length + offerMarkers.length + 1}
              key={`marker-${mapSearchMarker.item.id}`}
              item={mapSearchMarker.item}
              position={mapSearchMarker.position}
              active={
                exploreItemSelectionId(mapSearchMarker.item.id) === currentSelectionId
              }
              saved={savedExploreItemsByPlaceId.has(
                mapSearchMarker.item.place.id!,
              )}
            />
          )}
          {POIItem && (
            <GooglePOIMarker
              active={exploreItemSelectionId(POIItem.id) === currentSelectionId}
              zIndex={tripItemAndExploreMapElements.length + offerMarkers.length + 2}
              item={POIItem}
              key={`marker-${POIItem.id}`}
              position={POIItem.place.point}
            />
          )}

          {showLayersButton && <MapLayersButton />}

          <CardContainer ref={cardContainerRef}>
            {!activeExploreMarker &&
              !activeTripItemMarker &&
              mapSearchMarker &&
              exploreItemSelectionId(mapSearchMarker.item.id) === currentSelectionId &&
              currentSelectionId && ( // its a searched for marker that is active.
              (<SearchCardSingle
                onSave={clearMapSearchItem}
                selectedMarker={mapSearchMarker}
              />)
            )}
            {!activeExploreMarker &&
              !activeTripItemMarker &&
              POIItem &&
              exploreItemSelectionId(POIItem.id) === currentSelectionId &&
              currentSelectionId && ( // its a POI marker that is active.
              (<GooglePOICardSingle selectedMarker={fullPoiItems?.[0] || POIItem} />)
            )}
            {!useCardCarousel && activeExploreMarker && (
              <ExploreCardSingle selectedMarker={activeExploreMarker} />
            )}
            {useCardCarousel && activeExploreMarker && (
              <ExploreCardCarousel exploreItems={exploreItems} selectedMarker={activeExploreMarker} />
            )}
            {activeTripItemMarker && (
              <TripItemCardSingle selectedItem={activeTripItemMarker.item} />
            )}
            {activeOfferMarker && (
              <LEOfferCard offer={activeOfferMarker.offer} />
            )}
          </CardContainer>

          {tripDates.map(date => {
            const hasSelectedItemOnThisDate = activeTripItemMarker &&
              moment(activeTripItemMarker.item.startDate).format(ISO_DATE_FORMAT) === date
            return (hasSelectedItemOnThisDate || showAllRoutes) ?
                <DayRoutes key={date} trip={trip} date={date} selectedTripItemId={activeTripItemMarker?.item.id} /> :
              null
          })}

        </GoogleMap>
      )}
      {!isMapsContextLoaded && <LoadingIndicator floating />}
      {isMapsContextLoaded && items.length === 0 && canEdit && (
        <ModalContainer>
          <TooltipModal tripId={tripId} />
        </ModalContainer>
      )}
    </TripMapContext.Provider>
  )
}

const mapStateToProps = (state: App.State, ownProps: MapProps) => ({
  currentSelectionId: state.tripPlanner.currentSelectionId,
  ...ownProps,
})

const connector = connect(mapStateToProps)
export default connector(TripMap)
